/* eslint-disable react/prop-types */
import {
  EditOutlined,
  FileTextOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Spin,
  Tabs,
  Typography,
  message,
} from 'antd';
import { getPredio } from 'api/catastro/predio';
import { getContribuyente, getContribuyenteCaja } from 'api/empresas/contribuyentes';
import { getEstadosDelCreditoPae } from 'api/pae';
import { getAutoridadesDeterminantes } from 'api/pae/autoridadesDeterminantes';
import {
  baseURI,
  deleteCredito,
  getCredito,
  getCreditos,
  patchCredito,
  postCredito,
} from 'api/pae/creditos';
import { getOrigenesDeCredito } from 'api/pae/origenesDeCredito';
import { getTiposDeCredito } from 'api/pae/tiposDeCredito';
import { getSistemas } from 'api/recaudacion/sistemas';
import { getPadronesMap } from 'api/recaudacion/utils';
import FormSubmitControls from 'components/FormSubmitControls';
import { InputSearchPro } from 'components/InputSearch';
import ModalDelete from 'components/ModalDelete';
import NumericInput from 'components/NumericInput';
import Select from 'components/Select';
import Table from 'components/Table';
import Moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import makeMessager from 'utils/complexFormsMessages';
import getFormattedValues, { discartNullValues } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import keyGenerator from 'utils/keyGenerator';
import { capitalize } from 'utils/normalizers';
import padronToString, { contribuyenteToString } from 'utils/padronesToString';
import { hasAdvanceSearchPermissions, hasPadronPermission } from 'utils/permission/models';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import currencyFormatter from 'utils/toCurrency';
import ContribuyenteDescriptor from 'views/PAE/components/ContribuyenteDescriptor';
import PadronDescription from 'views/PAE/components/PadronDescription';
import SearchForm from 'views/PAE/CreditosFiscales/components/SearchForm';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import { getContenTypePorTipoDePadron } from 'api/catalogos';
import { ESTADOS_GLOBALES_MAP } from 'utils/estadosGlobales';
import ReadOnlyTabGeneral from './components/ReadOnlyTabGeneral';

const FormSection = styled(Row).attrs({
  gutter: [10, 10],
})`
`;

const { TabPane } = Tabs;

export const permissionConfig = {
  permissionModel: 'creditopae',
  catalogsModels: [
    'actuacionpae',
    'origendelcreditopae',
    'estatudeejecucionpae',
    'padron',
  ],
};

const CreditosFiscales = ({ permission }) => {
  const { estadosGlobales, estadosDeCreditosPae } = useSelector((state) => state.catalogs);
  const { padrones: _padrones } = useSelector((s) => s.catalogs);
  const padrones = _padrones.filter((p) => hasPadronPermission(p.id)
  && p.id !== 5 && p?.usado_en_ejecuciones);
  const { padron_principal } = useSelector(({ auth }) => auth.entidad);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mutable, setMutable] = useState(true);
  const [origenesDeCredito, setOrigenesDeCredito] = useState([]);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [tipoDePadron, setTipoDePadron] = useState(padron_principal || 1);
  const [visibleBusquedaPadrones, setVisibleBusquedaPadrones] = useState(false);
  const [padronFound, setPadronFound] = useState(null);
  const [dataExterno, setDataExterno] = useState([]);
  const [data, setData] = useState({ count: 0, results: [] });
  const [params, setParams] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const [currentCredito, setCurrentCredito] = useState(null);

  const [padronFoundBusqueda, setPadronFoundBusqueda] = useState(null);
  const [tipoDePadronBusqueda, setTipoDePadronBusqueda] = useState(padron_principal || 1);
  const [visibleBusquedaContribuyente, setVisibleBusquedaContribuyente] = useState(false);
  const [contribuyenteFound, setContribuyenteFound] = useState(null);
  const [sistemas, setSistemas] = useState([]);
  const [tiposDeCreditos, setTiposDeCreditos] = useState([]);
  const [estadosDeCreditos, setEstadosDeCreditos] = useState([]);
  const [autoridadesDeterminantes, setAutoridadesDeterminantes] = useState([]);

  const [formSearch] = Form.useForm();
  const [formGeneral] = Form.useForm();
  const [formCredito] = Form.useForm();

  const origenDeCreditoSelected = Form.useWatch('origen_del_credito', formGeneral);
  const permiteMultiplesEjecuciones = Form.useWatch('permitir_relacion_a_multiples_ejecuciones', formGeneral);

  const formsInstances = [
    formGeneral,
    formCredito,
  ];

  const basicKeys = [
    'general',
    'credito',
  ];

  const complexForms = [
  ];

  const displayTitle = (id) => {
    // Validación de entrada
    if (!id || typeof id !== 'number') {
      return 'Padrón';
    }

    // Búsqueda optimizada con early return
    const padron = padrones.find((p) => p?.id === id);

    // Validación del resultado
    if (padron?.descripcion) {
      return `Búsqueda de ${padron.descripcion}`;
    }

    // Fallback con información adicional
    if (padron) {
      return padron.nombre || padron.titulo || 'Padrón sin descripción';
    }

    return 'Padrón no encontrado';
  };

  const fetchData = async (page = 1) => {
    setLoading(true);
    setCurrentPage(page);
    const res = await getCreditos({ ...params, page }, false);
    setData(res);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    const fetchAll = async () => {
      setLoading(true);
      const [
        _origenesDeCredito,
        _sistemas,
        _tiposDeCredito,
        _estadosDeCreditos,
        _autoridadesDeterminantes,
      ] = await Promise.all([
        getOrigenesDeCredito(),
        getSistemas(),
        getTiposDeCredito(),
        getEstadosDelCreditoPae(),
        getAutoridadesDeterminantes(),
      ]);

      if (mounted) {
        setOrigenesDeCredito(_origenesDeCredito
          .filter((e) => e.padron && e.estados_globales !== ESTADOS_GLOBALES_MAP.CANCELADO));
        setSistemas(_sistemas);
        setTiposDeCreditos(_tiposDeCredito
          .filter((e) => e.estados_globales !== ESTADOS_GLOBALES_MAP.CANCELADO));
        setEstadosDeCreditos(_estadosDeCreditos
          .filter((e) => e.estados_globales !== ESTADOS_GLOBALES_MAP.CANCELADO));
        setAutoridadesDeterminantes(_autoridadesDeterminantes
          .filter((e) => e.estados_globales !== ESTADOS_GLOBALES_MAP.CANCELADO));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    formGeneral.resetFields();
    formCredito.resetFields();
    setPadronFound(null);
    setTipoDePadron(padron_principal || 1);
    setCurrentTabKey('general');
    setContribuyenteFound(null);
    setCurrentCredito(null);
  };
  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);

      // Validamos los formularios
      await formGeneral.validateFields();
      await formCredito.validateFields();

      if (!contribuyenteFound) {
        message.warn('Debe seleccionar un contribuyente.');
        return;
      }
      // Obtenemos los valores de los formularios
      const values = {
        ...formGeneral.getFieldsValue(),
        ...formCredito.getFieldsValue(),
      };

      // Obtenemos el ID de la ejecución
      const [key] = selectedRowKeys;

      // Guardamos el crédito
      const saved = await (key ? patchCredito(key, values) : postCredito({
        credito: values,
        tipo_de_padron: tipoDePadron,
        contribuyente: contribuyenteFound,
        padron: padronFound,
      }));

      if (saved) {
        await fetchData();
        if (_continue) {
          // eslint-disable-next-line no-use-before-define
          onClickEdit(saved.id);
        } else {
          onCancel();
        }
      }
    } catch (err) {
      onError(err, setLoading, formsInstances, setCurrentTabKey);
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteCredito(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    keyGenerator(data?.results, formGeneral);
    setVisible(true);
  };

  const onClickEdit = async (id) => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys || [id];
      const match = await getCredito(key);

      if (match) {
        formGeneral.setFieldsValue(match);
        formCredito.setFieldsValue(match);
        setCurrentCredito(match);
        setVisible(true);
      }
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      titleText: 'Folio ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      align: 'center',
      render: (text) => (
        <Typography.Text strong>
          {text}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Fecha de Registro',
      dataIndex: 'fecha_de_registro_del_credito',
      key: 'fecha_de_registro_del_credito',
      width: 250,
      align: 'center',
      render: (text) => (
        <Typography.Text code>
          {text ? new Date(text).toLocaleDateString('es-MX') : '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Origen de Crédito',
      dataIndex: 'origen_del_credito',
      key: 'origen_del_credito',
      width: 180,
      render: (text) => (
        <Typography.Text>
          {origenesDeCredito.find((e) => e.id === text)?.descripcion}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 120,
      align: 'center',
      render: (text) => (
        <Typography.Text code>
          {text}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Folio Documento Determinante',
      dataIndex: 'folio_de_documento_determinante',
      key: 'folio_de_documento_determinante',
      width: 250,
      align: 'center',
      render: (text) => (
        <Typography.Text code>
          {text || '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Fecha de la Determinante',
      dataIndex: 'fecha_de_la_determinante',
      key: 'fecha_de_la_determinante',
      width: 250,
      align: 'center',
      render: (text) => (
        <Typography.Text>
          {text ? new Date(text).toLocaleDateString('es-MX') : '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Fecha de Recepción',
      dataIndex: 'fecha_recepcion_de_la_determinante',
      key: 'fecha_recepcion_de_la_determinante',
      width: 250,
      align: 'center',
      render: (text) => (
        <Typography.Text>
          {text ? new Date(text).toLocaleDateString('es-MX') : '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Fecha de Obligación Inicial',
      dataIndex: 'fecha_de_obligacion_inicial_de_adeudos',
      key: 'fecha_de_obligacion_inicial_de_adeudos',
      width: 250,
      align: 'center',
      render: (text) => (
        <Typography.Text>
          {text ? new Date(text).toLocaleDateString('es-MX') : '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Total Determinante',
      dataIndex: 'total_determinante',
      key: 'total_determinante',
      width: 250,
      align: 'right',
      render: (text) => (
        <Typography.Text strong>
          {text ? currencyFormatter(text) : '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Permite multiples ejecuciones',
      dataIndex: 'permitir_relacion_a_multiples_ejecuciones',
      key: 'permitir_relacion_a_multiples_ejecuciones',
      width: 250,
      render: (text) => (
        <Typography.Text strong>
          {text ? 'Si' : 'No'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Tipo de Padrón',
      dataIndex: 'tipo_de_padron',
      key: 'tipo_de_padron',
      width: 180,
      render: (text) => (
        <Typography.Text>
          {padrones.find((e) => e.id === text)?.descripcion}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Clave del Padrón',
      dataIndex: 'clave_del_padron',
      key: 'clave_del_padron',
      width: 180,
      align: 'center',
      render: (text) => (
        <Typography.Text code>
          {text}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Clave Secundaria',
      dataIndex: 'clave_secundaria_del_padron',
      key: 'clave_secundaria_del_padron',
      width: 200,
      align: 'center',
      render: (text) => (
        <Typography.Text code>
          {text || '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Nombre/Razón Social',
      dataIndex: 'nombres_completos_del_padron',
      key: 'nombres_completos_del_padron',
      width: 250,
      ellipsis: true,
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }}>
          {text}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Total del Adeudo',
      dataIndex: 'monto_del_adeudo',
      key: 'monto_del_adeudo',
      width: 200,
      align: 'right',
      render: (text) => (
        <Typography.Text strong>
          {text ? currencyFormatter(text) : '-'}
        </Typography.Text>
      ),
    },
  ];

  const rules = {
    required: [{ required: true }],
    conditionalRequired: [{
      required: !permiteMultiplesEjecuciones,
    }],
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys,
    complexForms,
    currentTabKey,
    setCurrentTabKey,
    showMsg,
  );

  const formatPadrones = async (_padron) => {
    setLoading(true);
    let padron = _padron;
    if (tipoDePadron === padronesEnum.PREDIO) {
      const predio = await getPredio(_padron.id);
      padron = {
        ..._padron,
        ...predio,
      };
    }
    const nombre_completo = padronToString(padron, tipoDePadron);
    setPadronFound({ ...padron, nombre_completo });
    formGeneral.setFieldsValue({
      padron_id: padron.id,
      direccion_del_padron_id: padron?.direccion_index?.id || padron?.direccion?.id,
    });
    setLoading(false);
  };

  const handleSearchPadron = async () => {
    try {
      setLoading(true);
      const { q_padron } = formGeneral.getFieldsValue();

      if (!q_padron) {
        message.warn('Ingrese al menos un caracter para buscar.');
        setPadronFound([]);
        return;
      }

      const found = await getPadronesMap[tipoDePadron]({ q: q_padron });
      if (found?.length === 1 || found?.results?.length === 1) {
        const [first] = found?.results || found;

        let predio = null;
        if (tipoDePadron === padronesEnum.PREDIO) {
          predio = await getPredio(first.id);
        }

        await formatPadrones({
          ...first,
          ...predio,
        });
      } else if (found?.results?.length > 1) {
        setDataExterno(found);
        setVisibleBusquedaPadrones(true);
        setPadronFound([]);
      } else {
        message.warn('No se encontró ningún padron relacionado.');
        setPadronFound([]);
      }
    } catch (error) {
      onError(error, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const resetAndValidate = () => {
    setPadronFound([]);
    setDataExterno([]);
    setVisibleBusquedaPadrones(false);
    setTipoDePadron(padron_principal || 1);
    formGeneral.resetFields();
  };

  const handleSearchCreditos = async () => {
    setLoading(true);
    try {
      const formData = formSearch.getFieldsValue();
      const formattedData = getFormattedValues(discartNullValues(formData));
      if (formattedData?.tipo_de_padron) {
        const contType = await getContenTypePorTipoDePadron(formattedData?.tipo_de_padron);
        if (contType) {
          formattedData.content_type_padron = contType;
        }
        delete formattedData.tipo_de_padron;
      }
      formattedData.page = 1;
      setCurrentPage(1);

      setParams(formattedData);
      const res = await getCreditos(formattedData, false);
      setData(res);
      setSelectedRowKeys([]);
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const onChangeOrigenDeCredito = async (value) => {
    try {
      setLoading(true);

      const origen = origenesDeCredito.find((e) => e.id === value);

      formGeneral.setFieldsValue({
        tipo_de_padron: origen?.tipo_de_padron,
        sistema: origen?.sistema,
      });
      setTipoDePadron(origen?.tipo_de_padron);
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);
  const _onFinish = getOnFinish();

  const resetAndValidateContribuyente = () => {
    setContribuyenteFound(null);
    formGeneral.setFieldsValue({ contribuyente: null });
  };

  const searchContribuyente = async (_q) => {
    setLoading(true);
    const q = _q || formGeneral.getFieldValue('q_contribuyente');
    if (!q) {
      message.warn('Ingrese al menos un caracter para buscar.');
      setContribuyenteFound(null);
      formGeneral.setFieldsValue({ contribuyente: null });
      return;
    }
    const _contribuyentes = await getContribuyenteCaja({ q });
    if (_contribuyentes.length || _contribuyentes.count) {
      if ((_contribuyentes.length || _contribuyentes.count) === 1) {
        const _contribuyenteFound = _contribuyentes.results?.[0] || _contribuyentes[0];
        setContribuyenteFound(_contribuyenteFound);
        formGeneral.setFieldsValue({ contribuyente: _contribuyenteFound.id });
      } else {
        message.warn('Ingrese una búsqueda más específica');
      }
    } else {
      message.warn('No se encontró el padrón');
    }
    setLoading(false);
  };

  const handleCallbackSearchContribuyente = (padron) => {
    setContribuyenteFound(padron);
    formGeneral.setFieldsValue({ contribuyente: padron.id });
  };

  const handleOnChangeUsarContribuyenteDelPadron = async (value) => {
    if (!value) {
      setContribuyenteFound(null);
      formGeneral.setFieldsValue({ contribuyente: null });
      return;
    }

    if (!padronFound?.id) {
      message.warn('No se ha seleccionado un padrón');
      formGeneral.setFieldsValue({ usar_contribuyente_del_padron: false });
      setLoading(false);
      return;
    }

    if (!padronFound?.contribuyente?.id) {
      message.warn('El padrón no tiene un contribuyente relacionado');
      formGeneral.setFieldsValue({ usar_contribuyente_del_padron: false });
      setLoading(false);
      return;
    }

    setLoading(true);
    const contribuyente = await getContribuyente(padronFound?.contribuyente?.id
      || padronFound?.contribuyente);

    if (!contribuyente) {
      message.warn('No se encontró el contribuyente');
      setLoading(false);
      return;
    }

    setContribuyenteFound(contribuyente);
    formGeneral.setFieldsValue({ contribuyente: contribuyente?.id });
    setLoading(false);
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin
        tip="Cargando..."
        spinning={loading}
      >
        {!visible && (
          <>
            <SearchForm
              handleSearchData={handleSearchCreditos}
              setData={setData}
              setParams={setParams}
              loading={loading}
              origenesDeCredito={origenesDeCredito}
              // estatusDeEjecucionesList={estatusDeEjecucionesList}
              tipoDePadron={tipoDePadronBusqueda}
              setTipoDePadron={setTipoDePadronBusqueda}
              formSearch={formSearch}
              padronFound={padronFoundBusqueda}
              setPadronFound={setPadronFoundBusqueda}
              estadosDeCreditosPae={estadosDeCreditosPae}
            />
            <Table
              cols={columns}
              data={data?.results}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              allowSearch={false}
              customActions={[
                {
                  icon: <PlusOutlined />,
                  text: 'Agregar',
                  onClick: onClickAdd,
                  hasPermission: permission?.add,
                  disabledBySelection: false,
                },
                {
                  icon: <EditOutlined />,
                  text: 'Editar',
                  onClick: onClickEdit,
                  hasPermission: permission?.change,
                  disabled: selectedRowKeys.length > 1,
                },
              ]}
              rowKey="id"
              permission={permission}
              hasAllViewPermission
              pagination={false}
              pageSize={10}
              allowColumnSelector={false}
              count={data?.count}
              paginationCallback={fetchData}
              currentPage={currentPage}
            />
          </>
        )}
        {visible && (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Reporte Moroso`}
            extra={(
              <FormSubmitControls
                onFinish={_onFinish}
                onCancel={onCancel}
                loading={loading}
                mutable={mutable}
                permission={permission}
                selectedRowKeys={selectedRowKeys}
                baseURI={baseURI}
                callback={(id) => {
                  const [key] = selectedRowKeys;
                  const normalized = data?.results.map((e) => {
                    if (e.id === key) {
                      const match = estadosGlobales.find((eg) => eg.id === id);
                      formGeneral.setFieldsValue({ estados_globales: match.id });
                      setMutable(true);
                      return { ...e, estados_globales: id };
                    }
                    return e;
                  });
                  setData({ ...data, results: normalized });
                }}
              />
            )}
            bordered={false}
          >
            <Tabs
              onChange={mutable ? onChangeTabKey : setCurrentTabKey}
              activeKey={currentTabKey}
              type="card"
            >
              <TabPane
                tab={(
                  <>
                    <FileTextOutlined />
                    Datos del Reporte Moroso
                  </>
                )}
                key="general"
                forceRender
              >
                <Form
                  layout="vertical"
                  name="general"
                  form={formGeneral}
                  onFinish={(_, _continue) => _onFinish(_continue)}
                  scrollToFirstError={{
                    behavior: 'smooth',
                    block: 'center',
                  }}
                  initialValues={{
                    estados_globales: 1,
                    tipo_de_padron: padron_principal,
                    usar_contribuyente_del_padron: false,
                    fecha_de_registro_del_credito: new Moment(),
                    estado_del_credito: 1,
                    permitir_relacion_a_multiples_ejecuciones: false,
                  }}
                >
                  <br />
                  <FormSection>
                    {!!selectedRowKeys.length && (
                      <Col xs={24} md={8}>
                        <Form.Item
                          name="id"
                          label="ID"
                        >
                          <Input disabled />
                        </Form.Item>
                      </Col>
                    )}
                    {!!selectedRowKeys.length && (
                      <Col xs={24} md={8}>
                        <Form.Item
                          name="clave"
                          label="Clave"
                        >
                          <Input
                            allowClear
                            disabled
                          />
                        </Form.Item>
                      </Col>
                    )}
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="origen_del_credito"
                        label="Origen de Crédito"
                        rules={rules.required}
                      >
                        <Select
                          dataSource={origenesDeCredito}
                          allowClear
                          disabled={!mutable || selectedRowKeys.length}
                          onChange={onChangeOrigenDeCredito}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="fecha_de_registro_del_credito"
                        label="Fecha y hora"
                      >
                        <DatePicker
                          showTime
                          allowClear
                          format="DD/MM/YYYY HH:mm"
                          disabled
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="sistema"
                        label="Sistema"
                        rules={rules.required}
                      >
                        <Select
                          dataSource={sistemas}
                          allowClear
                          disabled
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="tipo_de_credito"
                        label="Tipo de Crédito"
                        rules={rules.required}
                      >
                        <Select
                          dataSource={tiposDeCreditos}
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="estado_del_credito"
                        label="Estado del Crédito"
                      >
                        <Select
                          dataSource={estadosDeCreditos}
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="numero_de_cuenta_del_credito"
                        label="Número de cuenta"
                      >
                        <Input
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                  </FormSection>
                  {!selectedRowKeys.length && (
                    <>
                      <br />
                      {!!origenDeCreditoSelected && (
                      <FormSection>
                        <Col span={24}>
                          <Row justify="space-between">
                            <Typography.Title level={4}>Padrón</Typography.Title>
                            <Col xs={24} md={8}>
                              <Form.Item
                                name="permitir_relacion_a_multiples_ejecuciones"
                                label="Permite múltiples reportes morosos"
                              >
                                <Select
                                  trueFalse
                                  disabled={!mutable || selectedRowKeys.length}
                                  onChange={(value) => {
                                    if (value) {
                                      setPadronFound([]);
                                      setDataExterno([]);
                                      setVisibleBusquedaPadrones(false);
                                      formGeneral.resetFields([
                                        'padron_id',
                                        'q_padron',
                                        'usar_contribuyente_del_padron',
                                      ]);
                                      formGeneral.setFieldsValue({
                                        padron_id: null,
                                        q_padron: null,

                                      });
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Col>
                        <InputSearchPro
                          rules={rules.conditionalRequired}
                          tooltip="Tipo de padrón definido por el origen de credito"
                          prefix={{
                            rules: rules.conditionalRequired,
                            style: { width: '100%' },
                            name: 'tipo_de_padron',
                            label: 'padron',
                            disabled: true,
                            dataSource: padrones,
                            onChange: (e) => setTipoDePadron(e),
                          }}
                          name="q_padron"
                          disabled={selectedRowKeys.length || permiteMultiplesEjecuciones}
                          onClickAdvanced={hasAdvanceSearchPermissions(tipoDePadron)
                            ? () => setVisibleBusquedaPadrones(true) : null}
                          label={displayTitle(tipoDePadron)}
                          inputProps={{
                            onPressEnter: () => handleSearchPadron(),
                          }}
                          breakPoints={{
                            xs: 24,
                            sm: 24,
                            md: 16,
                          }}
                          resultProps={{
                            breakPoints: {
                              xs: 24,
                              sm: 24,
                              md: 16,
                            },
                            name: 'padron_id',
                            render: (value) => padronToString(value, tipoDePadron),
                            label: displayTitle(tipoDePadron),
                            dataSource: padronFound,
                            onClear: resetAndValidate,
                          }}
                        />
                        <Form.Item hidden>
                          <Button htmlType="submit" />
                        </Form.Item>
                      </FormSection>
                      )}
                      <PadronDescription
                        padron={padronFound}
                        tipoDePadron={tipoDePadron}
                        setObservaciones={setPadronFound}
                      />
                      <br />
                      <FormSection>
                        <Col span={24}>
                          <Row justify="space-between">
                            <Typography.Title level={4}>Contribuyente</Typography.Title>
                            <Col xs={24} md={8}>
                              <Form.Item
                                name="usar_contribuyente_del_padron"
                                label="Usar contribuyente del padrón"
                              >
                                <Select
                                  trueFalse
                                  disabled={selectedRowKeys.length || permiteMultiplesEjecuciones}
                                  onChange={handleOnChangeUsarContribuyenteDelPadron}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Col>
                        <InputSearchPro
                          rules={rules.required}
                          label="Búsqueda Contribuyente"
                          name="q_contribuyente"
                          onClickAdvanced={() => setVisibleBusquedaContribuyente(true)}
                          breakPoints={{
                            xs: 24,
                            sm: 24,
                            md: 16,
                          }}
                          inputProps={{
                            onPressEnter: () => searchContribuyente(),
                            onChange: ({ target: { value } }) => {
                              if (!value) {
                                resetAndValidateContribuyente();
                              }
                            },
                          }}
                          resultProps={{
                            name: 'contribuyente',
                            label: 'Contribuyente',
                            rules: rules.required,
                            dataSource: contribuyenteFound,
                            render: (padron) => contribuyenteToString(padron),
                            keyLabelRender: false,
                            onClear: resetAndValidateContribuyente,
                            breakPoints: {
                              xs: 24,
                              sm: 24,
                              md: 16,
                            },
                          }}
                          disabled={!mutable}
                        />
                      </FormSection>
                      <ContribuyenteDescriptor
                        contribuyente={contribuyenteFound}
                        setObservaciones={setContribuyenteFound}
                      />
                      <br />
                      <Row gutter={10}>
                        <Col xs={24} md={8}>
                          <Form.Item
                            name="estados_globales"
                            label="Estado"
                            hasFeedback
                            hidden
                          >
                            <Select
                              dataSource={estadosGlobales}
                              allowClear
                              disabled
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )}
                  {!!selectedRowKeys.length && (
                    <ReadOnlyTabGeneral
                      credito={currentCredito}
                      setCredito={setCurrentCredito}
                    />
                  )}
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <>
                    <FileTextOutlined />
                    Datos del Crédito
                  </>
                )}
                key="credito"
                forceRender
              >
                <Form
                  layout="vertical"
                  name="credito"
                  form={formCredito}
                  onFinish={onFinish}
                  scrollToFirstError={{
                    behavior: 'smooth',
                    block: 'center',
                  }}
                  initialValues={{
                    es_credito_externo: false,
                  }}
                >
                  <br />
                  <FormSection>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="folio_de_documento_determinante"
                        label="Folio de Documento"
                      >
                        <Input
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="fecha_de_la_determinante"
                        label="Fecha de la Determinante"
                        rules={rules.required}
                      >
                        <DatePicker
                          showTime
                          allowClear
                          format="DD/MM/YYYY HH:mm"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="fecha_recepcion_de_la_determinante"
                        label="Fecha de Recepción"
                        rules={rules.required}
                      >
                        <DatePicker
                          showTime
                          allowClear
                          format="DD/MM/YYYY HH:mm"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="autoridad_determinante"
                        label="Autoridad Determinante"
                      >
                        <Select
                          dataSource={autoridadesDeterminantes}
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="fecha_de_obligacion_inicial_de_adeudos"
                        label="Fecha de Obligación Inicial de Adeudos"
                        rules={rules.required}
                      >
                        <DatePicker
                          showTime
                          allowClear
                          format="DD/MM/YYYY HH:mm"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <NumericInput
                        name="total_determinante"
                        label="Total de la Determinante"
                        currency
                        rules={rules.required}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="es_credito_externo"
                        label="Es crédito externo"
                        rules={rules.required}
                      >
                        <Select
                          trueFalse
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={16}>
                      <Form.Item
                        name="observaciones_del_credito"
                        label="Observaciones del crédito"
                        normalize={(value) => capitalize(value)}
                      >
                        <Input.TextArea
                          allowClear
                          disabled={!mutable}
                          autoSize={{ minRows: 3, maxRows: 6 }}
                          maxLength={500}
                        />
                      </Form.Item>
                    </Col>
                  </FormSection>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>

              </TabPane>
            </Tabs>

          </Card>
        )}

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            onCancel();
          }}
          visible={visibleAlert}
          content={` el reporte moroso ${formGeneral.getFieldValue('descripcion')}`}
          loading={loading}
        />

        <BusquedaAvanzadaPadrones
          visible={visibleBusquedaPadrones}
          setVisible={setVisibleBusquedaPadrones}
          callback={formatPadrones}
          dataExterno={dataExterno}
          setDataExterno={setDataExterno}
          tipoDePadron={tipoDePadron}
        />
        <BusquedaAvanzadaPadrones
          visible={visibleBusquedaContribuyente}
          setVisible={setVisibleBusquedaContribuyente}
          callback={handleCallbackSearchContribuyente}
          tipoDePadron={padronesEnum.CONTRIBUYENTE}
        />
      </Spin>
    </Row>
  );
};

export default CreditosFiscales;
